/**
 * Resumen en lenguaje llano del veredicto, con plantillas (sin LLM), citando los papers clave.
 *
 * Todo lo que dice sale de datos ya calculados (veredicto, pesos, diseños, respuestas), así
 * que no puede contradecir al titular ni inventar estudios.
 */

import { titleKey } from "./dedupe";
import { STANCE_SCORES } from "./evidence";
import type { Classification, Paper, PaperResult, Verdict } from "./models";

import type { Lang } from "./i18n";

export type { Lang };

export interface Citation {
  n: number;
  source_id: string;
  title: string;
  year: number | null;
  url: string | null;
}

export interface PlainSummary {
  text: string;
  citations: Citation[];
}

const T = {
  es: {
    opening: {
      yes: "La mayoría de la evidencia respalda que sí.",
      likely_yes: "La evidencia tiende a respaldar que sí, aunque con matices.",
      mixed: "Los estudios no se ponen de acuerdo.",
      no_evidence: "No hay pruebas sólidas en ningún sentido: la mayoría de los estudios no llegan a una conclusión.",
      no_effect: "La mayoría de los estudios no encuentran efecto.",
      likely_no: "La evidencia apunta a que no.",
      no: "La evidencia indica claramente que no.",
      insufficient: "Hay muy pocos estudios que respondan directamente a la pregunta, así que no se puede concluir nada.",
    } as Record<string, string>,
    shares: (fav: number, against: number, n: number, reviews: number) =>
      `Ponderando por la calidad de cada estudio, el ${fav} % de la evidencia está a favor de la afirmación y el ${against} % en contra o sin efecto ` +
      `(${n} ${n === 1 ? "estudio relevante" : "estudios relevantes"}, ${reviews} ${reviews === 1 ? "revisión sistemática" : "revisiones sistemáticas"}).`,
    backing: (what: string) => `La conclusión se apoya sobre todo en ${what}.`,
    strongestPro: (what: string) => `A favor, el estudio de más peso es ${what}.`,
    counter: (what: string, finding: string, ref: string) => `En sentido contrario, ${what} ${finding} ${ref}.`,
    two: (plural: string, a: string, b: string) => `dos ${plural} (${a} y ${b})`,
    plural: {
      meta_analysis: "revisiones sistemáticas", rct: "ensayos aleatorizados", quasi_experimental: "cuasi-experimentos",
      cohort: "estudios de cohortes", case_control: "estudios de casos y controles", observational: "estudios observacionales",
      cross_sectional: "estudios transversales", modelling: "estudios de modelización", animal_in_vitro: "estudios de laboratorio o en animales",
      narrative_review: "revisiones narrativas", qualitative: "estudios cualitativos", other: "estudios",
    } as Record<string, string>,
    design: {
      meta_analysis: "una revisión sistemática", rct: "un ensayo aleatorizado", quasi_experimental: "un cuasi-experimento",
      cohort: "un estudio de cohortes", case_control: "un estudio de casos y controles", observational: "un estudio observacional",
      cross_sectional: "un estudio transversal", modelling: "un estudio de modelización", animal_in_vitro: "un estudio de laboratorio o en animales",
      narrative_review: "una revisión narrativa", qualitative: "un estudio cualitativo", other: "un estudio",
    } as Record<string, string>,
    of: (year: number | null) => (year ? ` de ${year}` : ""),
    and: " y ",
    finding: {
      yes: "encontró un efecto", partial: "encontró un efecto solo en algunos casos", no_effect: "no encontró efecto",
      no: "encontró el efecto contrario", inconclusive: "no llegó a una conclusión",
    } as Record<string, string>,
    partialNote: "Muchos estudios solo encuentran el efecto en algunos grupos o de forma débil.",
    animalNote: "Buena parte de la evidencia viene de estudios de laboratorio o en animales, que no siempre se trasladan a personas.",
    noReviewsNote: "No hay revisiones sistemáticas entre los papers analizados, así que la conclusión es provisional.",
    lowCertaintyNote: (c: string) => `La certeza es ${c}: conviene tomar esta conclusión con cautela.`,
  },
  en: {
    opening: {
      yes: "Most of the evidence supports yes.",
      likely_yes: "The evidence tends to support yes, with some caveats.",
      mixed: "Studies disagree.",
      no_evidence: "There is no solid evidence either way: most studies are inconclusive.",
      no_effect: "Most studies find no effect.",
      likely_no: "The evidence points to no.",
      no: "The evidence clearly indicates no.",
      insufficient: "Very few studies answer the question directly, so no conclusion can be drawn.",
    } as Record<string, string>,
    shares: (fav: number, against: number, n: number, reviews: number) =>
      `Weighting each study by its quality, ${fav}% of the evidence supports the claim and ${against}% against or finds no effect ` +
      `(${n} relevant ${n === 1 ? "study" : "studies"}, ${reviews} systematic ${reviews === 1 ? "review" : "reviews"}).`,
    backing: (what: string) => `The conclusion rests mainly on ${what}.`,
    strongestPro: (what: string) => `In favour, the strongest study is ${what}.`,
    counter: (what: string, finding: string, ref: string) => `On the other hand, ${what} ${finding} ${ref}.`,
    two: (plural: string, a: string, b: string) => `two ${plural} (${a} and ${b})`,
    plural: {
      meta_analysis: "systematic reviews", rct: "randomized trials", quasi_experimental: "quasi-experiments",
      cohort: "cohort studies", case_control: "case-control studies", observational: "observational studies",
      cross_sectional: "cross-sectional studies", modelling: "modelling studies", animal_in_vitro: "laboratory or animal studies",
      narrative_review: "narrative reviews", qualitative: "qualitative studies", other: "studies",
    } as Record<string, string>,
    design: {
      meta_analysis: "a systematic review", rct: "a randomized trial", quasi_experimental: "a quasi-experiment",
      cohort: "a cohort study", case_control: "a case-control study", observational: "an observational study",
      cross_sectional: "a cross-sectional study", modelling: "a modelling study", animal_in_vitro: "a laboratory or animal study",
      narrative_review: "a narrative review", qualitative: "a qualitative study", other: "a study",
    } as Record<string, string>,
    of: (year: number | null) => (year ? ` from ${year}` : ""),
    and: " and ",
    finding: {
      yes: "found an effect", partial: "found an effect only in some cases", no_effect: "found no effect",
      no: "found the opposite effect", inconclusive: "reached no conclusion",
    } as Record<string, string>,
    partialNote: "Many studies find the effect only in some groups or only weakly.",
    animalNote: "Much of the evidence comes from laboratory or animal studies, which do not always translate to people.",
    noReviewsNote: "There are no systematic reviews among the analysed papers, so the conclusion is provisional.",
    lowCertaintyNote: (c: string) => `Certainty is ${c === "muy baja" ? "very low" : "low"}: take this conclusion with caution.`,
  },
};

type Scored = { paper: Paper; c: Classification };

const titleStem = (x: Scored) => titleKey(x.paper.title) ?? "";
const sameWork = (a: Scored, b: Scored) => {
  const [x, y] = [titleStem(a), titleStem(b)];
  return x.length > 0 && y.length > 0 && (x.startsWith(y) || y.startsWith(x));
};

const sideOf = (answer: string) => {
  const s = STANCE_SCORES[answer] ?? 0;
  return s > 0 ? "support" : s < 0 ? "against" : "neutral";
};

export function plainSummary(verdict: Verdict | null, results: PaperResult[], lang: Lang = "es", humanFocus = true): PlainSummary | null {
  if (!verdict) return null;
  const t = T[lang];
  const sentences: string[] = [t.opening[verdict.label] ?? t.opening.mixed!];
  const citations: Citation[] = [];
  if (verdict.label === "insufficient") return { text: sentences[0]!, citations };

  const pct = (x: number) => Math.round(x * 100);
  sentences.push(t.shares(pct(verdict.shares.support), pct(verdict.shares.against), verdict.n_relevant, verdict.reviews.total));

  // Con los pesos corregidos por fiabilidad (retractados fuera, grupos de autores amortiguados)
  const factor = new Map<string, number>();
  for (const k of verdict.corrections ?? []) for (const id of k.papers) factor.set(id, (factor.get(id) ?? 1) * k.factor);
  const relevant: Scored[] = results
    .filter((r) => r.classification && r.classification.answer in STANCE_SCORES && factor.get(r.paper.source_id) !== 0)
    .map((r) => ({ paper: r.paper, c: { ...r.classification!, weight: r.classification!.weight * (factor.get(r.paper.source_id) ?? 1) } }))
    .sort((a, b) => b.c.weight - a.c.weight);
  const cite = (x: Scored) => {
    const n = citations.length + 1;
    citations.push({ n, source_id: x.paper.source_id, title: x.paper.title, year: x.paper.year ?? null, url: x.paper.url ?? null });
    return `[${n}]`;
  };
  const describe = (x: Scored) => `${t.design[x.c.design] ?? t.design.other}${t.of(x.paper.year ?? null)}`;

  // Lado de la conclusión: el que respalda el titular
  const mainSide =
    ["yes", "likely_yes"].includes(verdict.label) ? "support"
    : ["no", "likely_no", "no_effect"].includes(verdict.label) ? "against"
    : verdict.label === "no_evidence" ? "neutral"
    : null;

  if (mainSide) {
    // Los dos de más peso, sin repetir el mismo trabajo publicado con títulos casi iguales
    const backers: Scored[] = [];
    for (const x of relevant) {
      if (sideOf(x.c.answer) !== mainSide || backers.some((b) => sameWork(b, x))) continue;
      if (backers.push(x) === 2) break;
    }
    if (backers.length === 2 && backers[0]!.c.design === backers[1]!.c.design) {
      const [a, b] = backers as [Scored, Scored];
      const year = (x: Scored) => `${x.paper.year ?? "?"} ${cite(x)}`;
      sentences.push(t.backing(t.two(t.plural[a.c.design] ?? t.plural.other!, year(a), year(b))));
    } else if (backers.length) {
      sentences.push(t.backing(backers.map((x) => `${describe(x)} ${cite(x)}`).join(t.and)));
    }
    // Contrapunto: el estudio de más peso del otro lado (si pesa algo)
    const otherSide = mainSide === "support" ? "against" : "support";
    const counter = relevant.find((x) => sideOf(x.c.answer) === otherSide && x.c.weight >= 0.3);
    if (counter && mainSide !== "neutral") {
      sentences.push(t.counter(describe(counter), t.finding[counter.c.answer] ?? "", cite(counter)));
    }
  } else {
    // Dividida: el estudio más sólido de cada lado
    const pro = relevant.find((x) => sideOf(x.c.answer) === "support");
    const con = relevant.find((x) => sideOf(x.c.answer) === "against");
    if (pro) sentences.push(t.strongestPro(`${describe(pro)} ${cite(pro)}`));
    if (con) sentences.push(t.counter(describe(con), t.finding[con.c.answer] ?? "", cite(con)));
  }

  // Matices
  const supportW = relevant.filter((x) => sideOf(x.c.answer) === "support").reduce((a, x) => a + x.c.weight, 0);
  const partialW = relevant.filter((x) => x.c.answer === "partial").reduce((a, x) => a + x.c.weight, 0);
  // Por número de estudios, no por peso: los de laboratorio ya pesan poco
  const lab = relevant.filter((x) => x.c.design === "animal_in_vitro" || x.c.humans < 0.5).length;
  if (mainSide === "support" && supportW && partialW / supportW >= 0.4) sentences.push(t.partialNote);
  if (humanFocus && relevant.length && lab / relevant.length >= 0.3) sentences.push(t.animalNote);
  if (verdict.reviews.total === 0) sentences.push(t.noReviewsNote);
  if (["baja", "muy baja"].includes(verdict.certainty) && verdict.label !== "no_evidence") sentences.push(t.lowCertaintyNote(verdict.certainty));

  return { text: sentences.join(" "), citations };
}
